import { useMemo } from 'react';

import { TFunction } from 'i18next';
import {
  CalendarIcon,
  CheckIcon,
  CircleDashedIcon,
  ClockIcon,
  LucideIcon,
  MessageSquareIcon,
  OctagonAlertIcon,
  PauseIcon,
  PhoneIcon,
  PlayIcon,
  RefreshCwIcon,
  RotateCwIcon,
  SparklesIcon,
  WorkflowIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { canFollowUpRun } from '@/modules/campaigns/util/campaignFollowUp';
import {
  formatNextActionTime,
  getNextActionShortLabel,
  getStopReasonLabel,
} from '@/modules/campaigns/util/nextAction';
import { getPermissions } from '@/redux/permissions/selectors';
import {
  CampaignStatus,
  ContactJourneyEntry,
  NBAOutput,
  NextAction,
  NextActionState,
} from '@/shared/types/campaigns';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getChannelDisplayName } from '@/util/channelName';
import { timeFromNowShort } from '@/util/dateUtils';

import { isVoiceChannel } from './ActivityTimeline';
import { useReevaluateCampaignSubscriber } from '../../hooks/useReevaluateCampaignSubscriber';

interface NextActionsSectionProps {
  journey: ContactJourneyEntry[];
  lifecycleStatus?: string;
  campaignStatus?: CampaignStatus;
  isStopped?: boolean;
  isResumable?: boolean;
  isAgentic?: boolean;
  onStop?: () => void;
  onResume?: () => void;
  /** From the orchestrator; when absent the section derives the follow-up from the journey. */
  nextAction?: NextAction;
  /** The contact's IANA time zone, used for absolute times when known. */
  timeZone?: string;
  campaignId?: string;
  subscriberId?: string;
}

// Absolute ms of an NBA action's scheduled follow-up, or undefined for a stop / no schedule.
const scheduleAtMsOf = (nba: NBAOutput): number | undefined => {
  const action = nba.action;
  if (!action || action.label === 'stop') {
    return undefined;
  }
  return (
    action.schedule_at ??
    (typeof action.reminder_seconds === 'number'
      ? nba.timestamp + action.reminder_seconds * 1000
      : undefined)
  );
};

const STATE_STYLE: Record<NextActionState, { icon: LucideIcon; color: string; bubble: string }> = {
  not_started: { icon: CalendarIcon, color: 'text-informational', bubble: 'bg-bg-info-soft' },
  deciding: { icon: SparklesIcon, color: 'text-ai', bubble: 'bg-bg-ai-soft' },
  in_conversation: {
    icon: MessageSquareIcon,
    color: 'text-successful',
    bubble: 'bg-bg-success-soft',
  },
  scheduled: { icon: ClockIcon, color: 'text-informational', bubble: 'bg-bg-info-soft' },
  retrying: { icon: RotateCwIcon, color: 'text-warning', bubble: 'bg-bg-warning-soft' },
  nothing_scheduled: {
    icon: CircleDashedIcon,
    color: 'text-warning',
    bubble: 'bg-bg-warning-soft',
  },
  on_hold: { icon: PauseIcon, color: 'text-foreground-muted', bubble: 'bg-bg-muted-50' },
  stopped: { icon: OctagonAlertIcon, color: 'text-destructive', bubble: 'bg-bg-destructive-soft' },
  finished: { icon: CheckIcon, color: 'text-successful', bubble: 'bg-bg-success-soft' },
  managed_by_workflow: {
    icon: WorkflowIcon,
    color: 'text-foreground-muted',
    bubble: 'bg-bg-muted-50',
  },
};

const NON_STOPPABLE_STATES: ReadonlySet<NextActionState> = new Set([
  'finished',
  'stopped',
  'managed_by_workflow',
]);

const HOLD_REASON_KEY: Record<string, string> = {
  paused: 'campaigns.subscriber_journey.next_action.hold_reason.paused',
  in_dispute: 'campaigns.subscriber_journey.next_action.hold_reason.in_dispute',
  legal_hold: 'campaigns.subscriber_journey.next_action.hold_reason.legal_hold',
};

const getRowText = (
  t: TFunction,
  nextAction: NextAction,
  timeZone?: string
): { label: string; detail?: string } => {
  const { state, due_at, channel } = nextAction;
  switch (state) {
    case 'not_started':
      return due_at
        ? {
            label: t('campaigns.subscriber_journey.next_action.campaign_starts', {
              0: formatNextActionTime(due_at, timeZone),
            }),
          }
        : { label: getNextActionShortLabel(t, nextAction) };
    case 'deciding':
      return { label: t('campaigns.subscriber_journey.next_action.deciding') };
    case 'in_conversation': {
      const detail = t('campaigns.subscriber_journey.next_action.next_check_after_conversation');
      if (isVoiceChannel(channel)) {
        return { label: t('campaigns.subscriber_journey.next_action.on_a_call'), detail };
      }
      return channel
        ? {
            label: t('campaigns.subscriber_journey.next_action.in_conversation', {
              0: getChannelDisplayName(channel),
            }),
            detail,
          }
        : { label: t('campaigns.subscriber_journey.next_action.in_conversation_generic'), detail };
    }
    case 'scheduled':
      return {
        label: due_at
          ? t('campaigns.subscriber_journey.next_action.next_attempt', {
              0: formatNextActionTime(due_at, timeZone),
            })
          : getNextActionShortLabel(t, nextAction),
        detail: t('campaigns.subscriber_journey.next_action.agent_picks_channel'),
      };
    case 'retrying':
      return { label: t('campaigns.subscriber_journey.next_action.retrying') };
    case 'nothing_scheduled':
      return { label: t('campaigns.subscriber_journey.next_action.no_further_contact') };
    case 'on_hold': {
      const holdKey = nextAction.hold_reason ? HOLD_REASON_KEY[nextAction.hold_reason] : undefined;
      return {
        label: holdKey
          ? t('campaigns.subscriber_journey.next_action.on_hold', { 0: t(holdKey) })
          : getNextActionShortLabel(t, nextAction),
      };
    }
    case 'stopped':
      return { label: getStopReasonLabel(t, nextAction.stop_reason) };
    case 'finished':
      return { label: t('campaigns.subscriber_journey.next_action.campaign_finished') };
    case 'managed_by_workflow':
      return { label: getNextActionShortLabel(t, nextAction) };
  }
};

// The last decision explains the state only when the AI agent produced it.
const AI_STOP_REASONS = new Set([
  'goal_achieved',
  'opt_out',
  'compliance',
  'campaign_ended',
  'nba_stop',
]);

const getWhy = ({ state, reason, stop_reason }: NextAction): string | undefined => {
  const explainsState =
    state === 'scheduled' ||
    state === 'in_conversation' ||
    (state === 'stopped' && !!stop_reason && AI_STOP_REASONS.has(stop_reason));
  if (!explainsState || !reason) {
    return undefined;
  }
  return reason.split(/\n\s*\n/)[0].trim();
};

const NextActionRow = ({ nextAction, timeZone }: { nextAction: NextAction; timeZone?: string }) => {
  const { t } = useTranslation();
  const { state, due_at } = nextAction;
  const why = getWhy(nextAction);
  const { label, detail } = getRowText(t, nextAction, timeZone);
  const style = STATE_STYLE[state];
  const Icon =
    state === 'in_conversation' && isVoiceChannel(nextAction.channel) ? PhoneIcon : style.icon;
  const showDueAt = !!due_at && state !== 'managed_by_workflow';

  return (
    <div className="flex gap-2 px-1 py-1.5 text-sm">
      <div className="w-16 shrink-0 pt-0.5">
        {state === 'deciding' && (
          <span className="animate-pulse text-ai">
            {t('campaigns.subscriber_journey.next_action.now')}
          </span>
        )}
        {state !== 'deciding' && showDueAt && (
          <TimestampTooltip timestamp={due_at}>
            <span className="text-foreground-muted">{timeFromNowShort(due_at)}</span>
          </TimestampTooltip>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-0.5">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
              style.bubble
            )}
          >
            <Icon size={16} className={style.color} />
          </div>
          <span className="font-medium">{label}</span>
        </div>
        {detail && <span className="pl-8 text-foreground-muted">{detail}</span>}
        {why && <span className="pl-8 text-foreground-muted">{why}</span>}
      </div>
    </div>
  );
};

export const NextActionsSection = ({
  journey,
  lifecycleStatus,
  campaignStatus,
  isStopped,
  isResumable,
  isAgentic,
  onStop,
  onResume,
  nextAction,
  timeZone,
  campaignId,
  subscriberId,
}: NextActionsSectionProps) => {
  const { t } = useTranslation();
  const { reevaluateSubscriber, reevaluateSubscriberStatus } = useReevaluateCampaignSubscriber();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.WRITE)
  );

  const upcomingScheduleAtMs = useMemo(() => {
    if (!canFollowUpRun(campaignStatus, lifecycleStatus)) {
      return null;
    }
    // The last NBA carries the active reminder; a newer NBA supersedes earlier ones.
    let lastNba: NBAOutput | undefined;
    for (const entry of journey ?? []) {
      if (entry.nba_output) {
        lastNba = entry.nba_output;
      }
    }
    const scheduleAtMs = lastNba && scheduleAtMsOf(lastNba);
    if (!scheduleAtMs || scheduleAtMs <= Date.now()) {
      return null;
    }
    return scheduleAtMs;
  }, [journey, lifecycleStatus, campaignStatus]);

  // When stopped, surface the follow-up that the stop cancelled (most recent scheduled action).
  const cancelledScheduleAtMs = useMemo(() => {
    if (!isStopped) {
      return null;
    }
    for (let i = (journey?.length ?? 0) - 1; i >= 0; i -= 1) {
      const nba = journey?.[i]?.nba_output;
      const scheduleAtMs = nba && scheduleAtMsOf(nba);
      if (scheduleAtMs) {
        return scheduleAtMs;
      }
    }
    return null;
  }, [journey, isStopped]);

  const followUpMs = upcomingScheduleAtMs ?? cancelledScheduleAtMs;
  const inProgress = campaignStatus === 'in_progress';
  const isStoppableState = !nextAction || !NON_STOPPABLE_STATES.has(nextAction.state);
  const showStop = !!isAgentic && inProgress && !isStopped && isStoppableState && !!onStop;
  const showResume = !!isAgentic && inProgress && !!isStopped && !!isResumable && !!onResume;
  const isNothingScheduled = nextAction?.state === 'nothing_scheduled';
  const showReevaluate = !!isAgentic && inProgress && !isStopped && isNothingScheduled && canWrite;

  const handleReevaluate = () => {
    if (campaignId && subscriberId) {
      reevaluateSubscriber({ campaignId, subscriberId });
    }
  };
  const showStoppedBadge = !!isStopped || nextAction?.state === 'stopped';

  // Render with a next-action context: the orchestrator's next action, a pending follow-up,
  // a stopped subscriber, or an active subscriber that can still be stopped.
  if (!isAgentic || (!nextAction && !upcomingScheduleAtMs && !isStopped && !showStop)) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold tracking-tight">
            {t('campaigns.subscriber_journey.next_actions')}
          </h2>
          {showStoppedBadge && (
            <Badge type="secondary" flavour="destructive" rounded={false}>
              {t('status.stopped')}
            </Badge>
          )}
          {isNothingScheduled && (
            <Badge type="secondary" flavour="warning" rounded={false}>
              {t('campaigns.subscriber_journey.next_action.short.nothing_scheduled')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4">
          {showReevaluate && (
            <Button
              variant="ghost-neutral"
              size="sm"
              onClick={handleReevaluate}
              disabled={reevaluateSubscriberStatus === 'pending'}
              className="h-auto min-w-0 gap-1 px-1"
            >
              <RefreshCwIcon />
              {t('campaigns.subscriber_journey.next_action.reevaluate')}
            </Button>
          )}
          {showStop && (
            <Button
              variant="destructive-ghost"
              size="sm"
              onClick={onStop}
              className="h-auto min-w-0 gap-1 px-1"
            >
              <PauseIcon />
              {t('campaigns.stop_all_actions')}
            </Button>
          )}
          {showResume && (
            <Button
              variant="ghost-neutral"
              size="sm"
              onClick={onResume}
              className="h-auto min-w-0 gap-1 px-1 text-informational hover:text-informational [&_svg]:text-informational"
            >
              <PlayIcon />
              {t('campaigns.resume_all_actions')}
            </Button>
          )}
        </div>
      </div>
      {nextAction && <NextActionRow nextAction={nextAction} timeZone={timeZone} />}
      {!nextAction && followUpMs && (
        <div className="flex items-center gap-2 px-1 py-1.5 text-sm">
          <div className="w-16 shrink-0">
            <TimestampTooltip timestamp={followUpMs}>
              <span className="text-foreground-muted">{timeFromNowShort(followUpMs)}</span>
            </TimestampTooltip>
          </div>
          <div
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
              isStopped ? 'bg-bg-muted-50' : 'bg-bg-info-soft'
            )}
          >
            <ClockIcon
              size={16}
              className={isStopped ? 'text-foreground-muted' : 'text-informational'}
            />
          </div>
          <span className={cn('font-medium', isStopped && 'text-foreground-muted line-through')}>
            {t('campaigns.subscriber_journey.scheduled_follow_up')}
          </span>
        </div>
      )}
    </div>
  );
};
